/**
 * Maaş Hesaplama Engine — 2026
 * Brüt → Net ve İşveren Maliyeti hesaplama
 *
 * Kaynaklar:
 * - 2026 Gelir Vergisi Dilimleri (GİB)
 * - SGK Prim Oranları
 * - Damga Vergisi: %0,759
 */

// ============================================================================
// 2026 SABİTLERİ
// ============================================================================

export const YILI = 2026;
export const BRUT_ASGARI_UCRET = 33_030; // 2026 brüt asgari ücret
export const SGK_TAVAN = 297_270; // 2026 SGK primine esas kazanç tavanı (aylık)

// SGK Oranları
export const SGK_ISCI_ORANI = 0.14; // %14
export const ISSIZLIK_ISCI_ORANI = 0.01; // %1
export const SGK_ISVEREN_ORANI = 0.2375; // %23.75 (MYÖ %12 + GSS %7.5 + KVSK %2.25)
export const ISSIZLIK_ISVEREN_ORANI = 0.02; // %2

// Vergi Oranları
export const DAMGA_VERGISI_ORANI = 0.00759; // %0,759

// 2026 Gelir Vergisi Dilimleri (Yıllık Kümülatif)
export const GELIR_VERGISI_DILIMLERI = [
  { ust: 190_000, oran: 0.15 },
  { ust: 400_000, oran: 0.2 },
  { ust: 1_500_000, oran: 0.27 },
  { ust: 5_300_000, oran: 0.35 },
  { ust: Infinity, oran: 0.4 },
];

// ============================================================================
// TİPLER
// ============================================================================

export interface MaasSonuc {
  brutMaas: number;
  sgkTavanUygulandi: boolean;
  // İşçi kesintileri
  sgkIsciPrimi: number;
  issizlikIsciPrimi: number;
  gelirVergisiMatrahi: number;
  gelirVergisi: number;
  damgaVergisi: number;
  toplamKesinti: number;
  netMaas: number;
  // İşveren payı
  sgkIsverenPrimi: number;
  issizlikIsverenPrimi: number;
  toplamIsverenMaliyeti: number;
  // Yüzdeler
  netOrani: number;
  isverenEkMaliyetOrani: number;
}

// ============================================================================
// HESAPLAMA FONKSİYONLARI
// ============================================================================

/**
 * Aylık kümülatif gelir vergisi hesapla
 * @param aylikMatrah - Aylık gelir vergisi matrahı
 * @param kumulatifMatrah - O aya kadar birikmiş yıllık matrah (önceki aylar)
 */
export function hesaplaGelirVergisi(
  aylikMatrah: number,
  kumulatifMatrah: number = 0
): number {
  const yillikMatrahSonrasi = kumulatifMatrah + aylikMatrah;

  const vergiSonrasi = hesaplaYillikVergi(yillikMatrahSonrasi);
  const vergiOncesi = hesaplaYillikVergi(kumulatifMatrah);

  return vergiSonrasi - vergiOncesi;
}

function hesaplaYillikVergi(yillikMatrah: number): number {
  let vergi = 0;
  let kalanMatrah = yillikMatrah;
  let oncekiUst = 0;

  for (const dilim of GELIR_VERGISI_DILIMLERI) {
    const dilimGenisligi = dilim.ust - oncekiUst;
    if (kalanMatrah <= 0) break;

    const buDilimdeki = Math.min(kalanMatrah, dilimGenisligi);
    vergi += buDilimdeki * dilim.oran;
    kalanMatrah -= buDilimdeki;
    oncekiUst = dilim.ust;
  }

  return vergi;
}

/**
 * Brüt maaştan net maaş ve işveren maliyeti hesapla
 * @param brutMaas - Aylık brüt maaş (TL)
 * @param ay - Kaçıncı ay (1-12), kümülatif vergi dilimine etkili
 */
export function hesaplaMaas(brutMaas: number, ay: number = 1): MaasSonuc {
  // SGK tavan kontrolü
  const sgkMatrah = Math.min(brutMaas, SGK_TAVAN);
  const sgkTavanUygulandi = brutMaas > SGK_TAVAN;

  // İşçi kesintileri
  const sgkIsciPrimi = sgkMatrah * SGK_ISCI_ORANI;
  const issizlikIsciPrimi = sgkMatrah * ISSIZLIK_ISCI_ORANI;

  // Gelir vergisi matrahı
  const gelirVergisiMatrahi = brutMaas - sgkIsciPrimi - issizlikIsciPrimi;

  // Kümülatif matrah (önceki aylar)
  const kumulatifMatrah = gelirVergisiMatrahi * (ay - 1);
  const gelirVergisi = hesaplaGelirVergisi(
    gelirVergisiMatrahi,
    kumulatifMatrah
  );

  // Damga vergisi
  const damgaVergisi = brutMaas * DAMGA_VERGISI_ORANI;

  // Toplam kesinti & Net
  const toplamKesinti =
    sgkIsciPrimi + issizlikIsciPrimi + gelirVergisi + damgaVergisi;
  const netMaas = brutMaas - toplamKesinti;

  // İşveren payı
  const sgkIsverenPrimi = sgkMatrah * SGK_ISVEREN_ORANI;
  const issizlikIsverenPrimi = sgkMatrah * ISSIZLIK_ISVEREN_ORANI;
  const toplamIsverenMaliyeti =
    brutMaas + sgkIsverenPrimi + issizlikIsverenPrimi;

  return {
    brutMaas,
    sgkTavanUygulandi,
    sgkIsciPrimi,
    issizlikIsciPrimi,
    gelirVergisiMatrahi,
    gelirVergisi,
    damgaVergisi,
    toplamKesinti,
    netMaas,
    sgkIsverenPrimi,
    issizlikIsverenPrimi,
    toplamIsverenMaliyeti,
    netOrani: (netMaas / brutMaas) * 100,
    isverenEkMaliyetOrani:
      ((toplamIsverenMaliyeti - brutMaas) / brutMaas) * 100,
  };
}

// ============================================================================
// FORMAT
// ============================================================================

export function formatTL(value: number): string {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
